// The built-in words a design may reword (layout.DesignWords, held to designWords.json).
// Lookup: the design's word → the organisation's wording → the shipped phrase.
import list from './designWords.json'
import { t, type DeskPill, type Language, type PhraseKey } from '../lib/kioskText'
import type { StaffKey } from '../lib/staffText'
import type { ElementKind, LayoutDocument, LayoutText } from './types'

export type DesignWordId = PhraseKey

export interface DesignWord {
  id: DesignWordId
  slip?: string
  elements: ElementKind[]
  /** A template: it may use any of these placeholders, or none. */
  optional?: string[]
}

export const DESIGN_WORDS = list as DesignWord[]

/** What the editor and Settings › Wording call each word. */
export const DESIGN_WORD_NAMES: Record<string, StaffKey> = {
  peopleAhead: 'designs.word.peopleAhead',
  nextInLine: 'designs.word.nextInLine',
  approxWait: 'designs.word.approxWait',
  validUntil: 'designs.word.validUntil',
  boardNoCalls: 'designs.word.boardNoCalls',
  deskCalled: 'designs.word.deskCalled',
  deskServing: 'designs.word.deskServing',
  deskFree: 'designs.word.deskFree',
  deskBackSoon: 'designs.word.deskBackSoon',
  deskClosed: 'designs.word.deskClosed',
  deskLabel: 'designs.word.deskLabel',
  inQueue: 'designs.word.inQueue',
}

export type Words = LayoutDocument['words']

/** A design's own word, in characters (layout.MaxWordLength). */
export const MAX_WORD_LENGTH = 120

/** The words an element draws, in the registry's order. */
export function wordsOf(kind: ElementKind): DesignWordId[] {
  return DESIGN_WORDS.filter((w) => w.elements.includes(kind)).map((w) => w.id)
}

/** The design's own word in lang, undefined when it has none. */
export function ownWord(words: Words, id: DesignWordId, lang: string): string | undefined {
  const v = words?.[id]?.[lang]
  return v && v.trim() ? v : undefined
}

/** The word as a screen draws it: the design's, else the organisation's or shipped one. */
export function wordIn(words: Words, id: DesignWordId, lang: string): string {
  return ownWord(words, id, lang) ?? t(id, lang as Language)
}

/** The design with its own word for id in lang set; empty text removes it. */
export function withWord(doc: LayoutDocument, id: DesignWordId, lang: string, text: string): LayoutDocument {
  const forId: LayoutText = { ...(doc.words?.[id] ?? {}) }
  if (text.trim()) forId[lang] = text
  else delete forId[lang]
  const words = { ...(doc.words ?? {}) }
  if (Object.keys(forId).length > 0) words[id] = forId
  else delete words[id]
  const { words: _old, ...rest } = doc
  return Object.keys(words).length > 0 ? { ...rest, words } : rest
}

/** "Ahead of you: 3", or "You are next" for nobody ahead. */
export function aheadLine(words: Words, ahead: number, lang: string): string {
  return ahead === 0 ? wordIn(words, 'nextInLine', lang) : `${wordIn(words, 'peopleAhead', lang)}: ${ahead}`
}

/** "Approximate wait: 9 min". */
export function waitWords(words: Words, wait: string, lang: string): string {
  return `${wordIn(words, 'approxWait', lang)}: ${wait}`
}

/** "In queue: 5", the hall's waiting total. */
export function inQueueWords(words: Words, n: number, lang: string): string {
  return wordIn(words, 'inQueue', lang).replace('{n}', String(n))
}

/** Whether the desk label in force is the desk's own name and nothing else. */
export function plainDeskLabel(words: Words, lang: string): boolean {
  return wordIn(words, 'deskLabel', lang).trim() === '{desk}'
}

/** A desk as boards and counter displays name it: "Masa 3" from `Masa {number}`. */
export function deskLabel(words: Words, lang: string, desk: { name?: string; number?: string }): string {
  const name = desk.name ?? ''
  const number = desk.number ?? ''
  if (!name && !number) return ''
  return wordIn(words, 'deskLabel', lang).replaceAll('{desk}', name).replaceAll('{number}', number).replace(/\s+/g, ' ').trim()
}

/** "Valid until 18:00"; the time is already on the branch's clock. */
export function validUntilWords(words: Words, time: string, lang: string): string {
  return wordIn(words, 'validUntil', lang).replace('{time}', time)
}

const PILL_WORDS: Record<DeskPill, DesignWordId> = {
  called: 'deskCalled',
  serving: 'deskServing',
  open: 'deskFree',
  paused: 'deskBackSoon',
  closed: 'deskClosed',
}

/** A counter display's status pill or desk status; a closed desk never says "Free". */
export function pillWords(words: Words, pill: DeskPill | undefined, lang: string): string {
  return wordIn(words, PILL_WORDS[pill ?? 'open'], lang)
}
